import React, { useState, useEffect } from 'react';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { get, set, shuffle, take, first, size } from 'global/lib/lodash';
import useStyles from './styles';
import { Text } from 'global/components/text';
import { Column, Row } from 'global/components/row';
import { Button } from 'global/components/Button';
import { ImageCarousel } from 'global/components/image';
import { Icon, Tooltip } from '@material-ui/core';
import SnackAlerts from 'global/components/snack-alerts';
import { BorderLinearProgress } from '../courses';
import { scrollTop, sortByOrderKey, sortAlphaNum, _size } from 'global/utils';
import clsx from 'clsx';
import withRouter from 'react-router-dom/withRouter';
import { Loader } from 'global/styles';
import NavToTop from 'feature/nav/NavToTop';
import DocumentContainer from '../DocumentContainer';
import { useUser } from 'global/hooks/useUser';
import { useWindowSize } from 'global/hooks/useWindowSize';

export const Checklist = withRouter((props) => {
  const { isMobile, windowSize } = useWindowSize();

  const classes = useStyles({ isMobile, windowSize });

  const { document, updateReadStatus } = props;

  const checklistid = get(document, 'documentableId');

  const { isReview } = get(props, 'location.state', {});

  const takeAmt = parseInt(get(document, 'meta.questionCount', 1000));
  const randomOptions = get(document, 'meta.randomOptions', false);
  const randomCheckpoints = get(document, 'meta.randomCheckpoints', false);
  const { me } = useUser();

  const [questions, setQuestions] = useState([]);
  const [state, setState] = useState({
    checklistid,
    currentQuestion: 0,
    answers: {},
    scoreValue: 0,
  });
  const [alerts, setAlerts] = useState();

  const isSBS = document.documentableType === 'step_by_step';
  const isQuiz = document.documentableType === 'checklist' && get(document,'meta.type') === 'quiz';
  const isChecklist = document.documentableType === 'checklist' && !isQuiz;
  const isAssessment = document.documentableType === 'assessment';
  const checklist = document.document;

  const transformData = (d) => {
    const da = first(d);
    if (isSBS) {
      da.checkpoints = da.items.sort(sortByOrderKey);
    } else if (isChecklist) {
      set(da, 'checkpoints', get(da, 'checkpoints', []).sort(sortAlphaNum));
      // Add checklist image to carousel for each individual checkpoint for non-quizzes
      if (size(get(da, 'images')) > 0) {
        get(da, 'checkpoints', []).forEach((cp, i) => {
          let images = get(cp, 'images') || [];
          images.push(get(da, 'images.0'));
          set(da, `checkpoints.${i}.images`, images);
        });
      }
    }
    return da;
  };

  const { data, loading, update } = useRetrieveData(
    {
      url: isSBS ? `/step-by-steps/${checklist.id}` : `/checklists/${checklist.id}`,
    },
    {
      useLangFilter: true,
      useEffect: true,
      build: isSBS ? ['stepBySteps'] : ['checklists'],
      params: {
        include: isSBS ? 'items,images,items.images' : 'images,checkpoints.checkpointOptions,checkpoints.images,classification.system',
      },
      transformData,
    }
  );

  const changeQuestion = (dir = 1) => {
    setState((s) => ({ ...s, currentQuestion: s.currentQuestion + dir }));
  };

  const setAnswer = (q) => {
    setState((s) => ({
      ...s,
      answers: { ...s.answers, [s.currentQuestion]: q },
      currentQuestion: s.currentQuestion + 1,
    }));
  };

  const submit = async () => {
    try {
      setState((s) => ({ ...s, loading: true }));

      await updateReadStatus(true);

      await Promise.all([props.update && props.update(), update()]);
      setState((s) => ({ ...s, showResults: true, currentQuestion: 0 }));
    } catch (err) {
      console.error(err);
      setAlerts({ message: err.message, severity: 'error' });
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  const generateQuestions = (reset = false) => {
    const cps = randomCheckpoints ? shuffle(data.checkpoints) : data.checkpoints;
    setQuestions(take(cps, takeAmt).map((q) => ({ ...q, checkpointOptions: randomOptions ? shuffle(q.checkpointOptions) : q.checkpointOptions })));
    setState((s) => ({
      ...s,
      answers: reset ? {} : s.answers,
      checklistid,
    }));
  };

  const isCurrentlyTakingQuiz = !!_size(state.answers) && state.currentQuestion > -1 && !state.showResults;

  useEffect(() => {
    const unblock = props.history.block((nextLocation) => {
      if (isCurrentlyTakingQuiz) {
        const canLeave = window.confirm(`Are you sure you want to leave? Your currently selected answers will be lost.`);
        return canLeave;
      }
    });

    return () => {
      unblock();
    };
  }, [isCurrentlyTakingQuiz]);

  useEffect(() => {
    if (data && !loading) {
      if ((state.checklistid !== checklistid && get(data, 'id') == checklistid) || !_size(questions)) {
        generateQuestions(state.checklistid !== checklistid);
      }
    }
  }, [data, checklistid, loading]);

  const prog = ((state.currentQuestion + 1) / (_size(questions) + 1)) * 100;

  const isCompleted = !!get(document, 'reads', []).find((r) => r.userId == me.id && !!r.completedAt);

  if (isCompleted) {
    return (
      <div className={clsx(classes.questionsContainer)}>
        <NavToTop onClick={() => scrollTop('#ActiveAbsolute')} />
        <DocumentContainer doc={document} isCourse={true} />
      </div>
    );
  }

  if (isReview && !loading) {
    return (
      <div className={classes.questionsContainer}>
        <div className="scrollContainer">
          <Question
            currentQuestion={0}
            answer={get(state.answers, state.currentQuestion, {})}
            question={{
              className: 'intro',
              title: 'Quiz',
              description: `This user has not yet taken this quiz.`,
            }}
            setAnswer={setAnswer}
          />
        </div>
      </div>
    );
  }

  return loading ? (
    <Column className={classes.questionsContainer}>
      <Loader />
    </Column>
  ) : (
    <div className={classes.questionsContainer}>
      <div className="scrollContainer">
        <Question currentQuestion={state.currentQuestion} answer={get(state.answers, state.currentQuestion, {})} question={get(questions, state.currentQuestion, {})} setAnswer={setAnswer} />
      </div>

      <Column className="buttonContainer" center={!isAssessment}>
        <div className="prog">
          <BorderLinearProgress variant="determinate" value={prog} />
        </div>

        <Row className="buttonRow">
          <Tooltip title={'Previous'}>
            <span>
              <Button disabled={state.currentQuestion <= 0} onClick={() => changeQuestion(-1)} startIcon={<Icon>arrow_left</Icon>}>
                Prev
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={'Next'}>
            <span>
              <Button onClick={state.currentQuestion === questions.length - 1 ? submit : () => changeQuestion(1)} startIcon={<Icon>arrow_right</Icon>}>
                Next
              </Button>
            </span>
          </Tooltip>
        </Row>
      </Column>
      <SnackAlerts alerts={alerts} setAlerts={setAlerts} />
    </div>
  );
});

const Question = (props) => {
  const { question } = props;

  const images = get(question, 'images', []);
  const hasImages = !!_size(images);

  const __html = (() => {
    const d = get(question, 'description', '');
    return `${d}`;
  })();

  const htmlWhy = (() => {
    let d = get(question, 'notes', '');
    d = typeof d === 'function' ? d() : d;
    return !d ? null : `<b>Why?</b> ${d}`;
  })();

  const buttons = get(question, 'buttons', []);

  return (
    <Row key={question.id} className={clsx('question', 'checkpoint', get(question, 'className', ''), hasImages && 'withImages')}>
      <Column className="textContainer">
        <Text className="header" type="title_large">
          {get(question, 'name', 'Criteria')}
        </Text>
        <Text className="description" type="title_small" dangerouslySetInnerHTML={{ __html }} />
        <Text className="why" type="title_small" dangerouslySetInnerHTML={{ __html: htmlWhy }} />
      </Column>

      {hasImages && (
        <div className="imageContainer">
          <Text className="header" type="title_large">
            {'Example'}
          </Text>
          <ImageCarousel images={images} />
        </div>
      )}

      {!!_size(buttons) && (
        <Row className="submitRow" center>
          {buttons.map((btn) => (
            <Button {...btn}>{btn.title}</Button>
          ))}
        </Row>
      )}
    </Row>
  );
};
